import ApplicationLayout from '@rio-cloud/rio-uikit/components/applicationLayout/ApplicationLayout';
import AssetTree from '@rio-cloud/rio-uikit/components/assetTree/AssetTree';
import TreeCategory from '@rio-cloud/rio-uikit/components/assetTree/TreeCategory';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { gaPush, SIDEBAR_ACTIONS, SIDEBAR_CATEGORY } from '@/configuration/setup/googleAnalytics';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { componentMap } from '@/routes/componentRoutes';
import { getNavigationRedirect } from '@/routes/handleLayoutRedirect';
import { selectCurrentLayout, setCurrentLayout } from '@/store/layout/layoutSlice';
import { LayoutItem, sidebarToContent } from '@/store/layout/sidebarToContent';
import { closeSidebar, selectIsSidebarOpen, selectScrollPosition, toggleSidebar } from '@/store/sidebar/sidebarSlice';

import { SCROLLBAR_CONTAINER } from './constants';
import { ChatAssetTreeWrapper } from './Style';

type ChatAssetTreeProps = {
  isLoading: boolean;
};

const ChatAssetTree = ({ isLoading }: ChatAssetTreeProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isTreeOpen = useAppSelector(selectIsSidebarOpen);
  const activeSidebarItem = useAppSelector(selectCurrentLayout);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const [isWide, setIsWide] = useState(false);

  const scrollPosition = useAppSelector(selectScrollPosition);

  // INFO: Manages the scroll position at the sidebar when operations an operation is triggered (effect for (scrollPosition changes)
  const scrollbarView = document.querySelector(SCROLLBAR_CONTAINER);
  if (scrollbarView) {
    scrollbarView.scrollTop = scrollPosition || 0;
  }

  useEffect(() => {
    if (!sidebarRef.current) return;

    const updateIsWide = () => {
      const sidebarWidth = sidebarRef.current?.offsetWidth ?? 0;
      const windowWidth = window.innerWidth;
      setIsWide(sidebarWidth / windowWidth > 0.6);
    };

    updateIsWide();
    window.addEventListener("resize", updateIsWide);

    return () => {
      window.removeEventListener("resize", updateIsWide);
    };
  }, [isTreeOpen, sidebarRef]);


  const navigate = useNavigate();

  useEffect(() => {
    if (!isTreeOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
          isWide &&
          sidebarRef.current &&
          !sidebarRef.current.contains(event.target as Node)
      ) {
        dispatch(closeSidebar());
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTreeOpen, isWide, dispatch]);


  const handleToggleTree = () => dispatch(toggleSidebar());
  const handleOnChange = (selectCategoryId: string) => {
    dispatch(setCurrentLayout(selectCategoryId as LayoutItem));

    gaPush({
      category: SIDEBAR_CATEGORY,
      action: SIDEBAR_ACTIONS.SELECT_CATEGORY,
      label: `Category ID: ${selectCategoryId}`,
    });

    navigate(getNavigationRedirect(selectCategoryId));
  };

  return (
    <ApplicationLayout.Sidebar>
      {activeSidebarItem ? (
       <ChatAssetTreeWrapper data-testid="sidebar-wrapper">
        <AssetTree
          minWidth={300}
          maxWidth={400}
          currentCategoryId={activeSidebarItem}
          onCategoryChange={handleOnChange}
          isOpen={isTreeOpen}
          onToggleTree={handleToggleTree}
          useOffscreen
          ref={sidebarRef}
        >
          {Object.entries(sidebarToContent).map(([key, item]) => {
            const SidebarComponent = componentMap[key as LayoutItem]?.sidebarComponent;

            return (
              <TreeCategory
                key={`${key}-sidebar`}
                id={key}
                label={intl.formatMessage({ id: item.translation })}
                icon={item.icon}
              >
                {SidebarComponent && <SidebarComponent isLoading={isLoading} />}
              </TreeCategory>
            );
          })}
        </AssetTree>
       </ChatAssetTreeWrapper>
      ) : null}
    </ApplicationLayout.Sidebar>
  );
};

export default ChatAssetTree;
